import * as Vue from "vue"
import { createPinia } from 'pinia'
import router from "controllers/vue_components/router"
import VCalendar from 'v-calendar'
import { useGlobalStore } from 'stores/global.js'
import { useNotificationsStore } from 'stores/notifications.js'
import { unlockAudio } from 'controllers/vue_components/notifications/notificationSounds.js'
import { HIGHCHARTS_THEME } from 'utils/highchartsTheme.js'

// Keep the AudioContext unlocked. Called on every click/keydown so that
// if the browser suspends it (e.g. after inactivity), the next interaction
// wakes it back up before any WebSocket-triggered sound tries to play.
document.addEventListener('click',   unlockAudio, true)
document.addEventListener('keydown', unlockAudio, true)
import AppSidebar from "controllers/vue_components/AppSidebar"
import SidebarLink from "controllers/vue_components/SidebarLink"
import EventsPage from "controllers/vue_components/events/EventsPage"
import AppShell from "controllers/vue_components/AppShell"

const point = "#calendar_app"
import Onboarding from "controllers/vue_components/onboarding"
import PublicEvent from "controllers/vue_components/public_event/index"
import PublicEventLegacy from "controllers/vue_components/public_event"
import Navbar from "controllers/vue_components/navbar"
import HomeFeatures from "controllers/vue_components/home_features"
import Availability from "controllers/vue_components/calendar/availability"
import FlashMessage from "controllers/vue_components/flash_message"
import ConnectedCalendars from "controllers/vue_components/connected_calendars"
import CopyText from "controllers/vue_components/copy_text"
import Event from "controllers/vue_components/events/event"
import EventsTable from "controllers/vue_components/events/index/table"
import Holiday from "controllers/vue_components/holiday"
import StatsCard from "controllers/vue_components/stats_card"
import BookingsList from "controllers/vue_components/events/booking/list"
import CancelBooking from "controllers/vue_components/events/booking/cancel"
import VueConfetti from "controllers/vue_components/vue_confetti"
import CookiesModal from "controllers/vue_components/cookies_modal"
import AttendeesList from "controllers/vue_components/events/booking/attendees/list"
import IntegrationsList from "controllers/vue_components/integrations/list"
import ExternalPayments from "controllers/vue_components/integrations/external_payments"

import Webhooks from "controllers/vue_components/integrations/webhooks"
import HomeSecondaryFeatures from "controllers/vue_components/home_secondary_features"
import AdminUsersList from "controllers/vue_components/admin/users/list"
import AdminBugsList from "controllers/vue_components/admin/bugs/list"
import AdminVerificationsList from "controllers/vue_components/admin/verifications/list"
import MyCalendarIndexPage from "controllers/vue_components/calendar/home"
import CalendarSettings from "controllers/vue_components/calendar/settings"
import TimelydoBottomModal from "controllers/vue_components/fields/timelydo_bottom_modal"
import BottomNav from "controllers/vue_components/BottomNav"
import SettingProfile from "controllers/vue_components/settings/v1"
import OrgIndex from "controllers/vue_components/organizations"
import OrganizationOnboarding from "controllers/vue_components/organizations/onboarding"
import MessageForm from "controllers/vue_components/public/public_message_form"
import Troubleshoot from "controllers/vue_components/public/troubleshoot"
import PricingIndex from "controllers/vue_components/pricing"
import SocialIcons from "controllers/vue_components/events/social_icon"
import UserInvitations from "controllers/vue_components/admin/users/invitations"
import AdminEmails from "controllers/vue_components/admin/emails/emails"
import QuillEditor from "controllers/vue_components/fields/quill_editor"
import NewEvent from "controllers/vue_components/events/new_event"

function pctFromElapsed(ms) {
  if (ms < 1000) return Math.floor(ms / 1000 * 30)
  if (ms < 3000) return Math.floor(30 + (ms - 1000) / 2000 * 30)
  return Math.min(Math.floor(60 + (1 - Math.pow(1 - Math.min((ms - 3000) / 8000, 1), 2)) * 39), 99)
}

// Org loader panel only (app/views/layouts/organization.html.erb) — no-op
// elsewhere since #app-loader-status doesn't exist on other layouts.
function stageFromPct(v) {
  if (v < 30) return 'Authenticating workspace'
  if (v < 60) return 'Loading organization'
  if (v < 90) return 'Preparing modules'
  return 'Finalizing'
}

function startPctCounter() {
  if (window._pctRafId) return  // inline script already running — let it continue
  const el = document.getElementById('app-loader-pct')
  if (!el) return
  const statusEl = document.getElementById('app-loader-status')
  // User layout only — the org loader's bar is a CSS scan animation, not a fill.
  const fillEl = document.getElementById('app-loader-fill')
  el.textContent = '0%'
  if (fillEl) {
    // Reset without transitioning backwards from the previous 100%.
    fillEl.style.transition = 'none'
    fillEl.style.width = '0%'
    void fillEl.offsetWidth
    fillEl.style.transition = ''
  }
  const startTime = performance.now()
  function tick(now) {
    const v = pctFromElapsed(now - startTime)
    el.textContent = v + '%'
    if (fillEl) fillEl.style.width = v + '%'
    if (statusEl) statusEl.textContent = stageFromPct(v)
    if (v < 99) window._pctRafId = requestAnimationFrame(tick)
    else window._pctRafId = null
  }
  window._pctRafId = requestAnimationFrame(tick)
}

function finishPctCounter() {
  if (window._pctRafId) { cancelAnimationFrame(window._pctRafId); window._pctRafId = null }
  // Snap to 100% so the count completes instead of freezing mid-number as it fades.
  const fillEl = document.getElementById('app-loader-fill')
  if (fillEl) {
    fillEl.style.width = '100%'
    const el = document.getElementById('app-loader-pct')
    if (el) el.textContent = '100%'
  }
}

function startAppProgress(startPct = true) {
  const bar = document.getElementById('app-top-bar')
  if (bar) {
    bar.style.transition = 'none'
    bar.style.opacity = '1'
    bar.style.width = '0%'
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bar.style.transition = 'width 0.4s ease-out'
        bar.style.width = '12%'
        setTimeout(() => {
          bar.style.transition = 'width 8s cubic-bezier(0.05, 0, 0, 1)'
          bar.style.width = '82%'
        }, 400)
      })
    })
  }
  if (startPct) startPctCounter()
}

function finishAppProgress() {
  finishPctCounter()
  const bar = document.getElementById('app-top-bar')
  if (!bar) return
  bar.style.transition = 'width 0.2s ease-out'
  bar.style.width = '100%'
  setTimeout(() => {
    bar.style.transition = 'opacity 0.35s ease'
    bar.style.opacity = '0'
    setTimeout(() => {
      bar.style.transition = 'none'
      bar.style.opacity = '1'
      bar.style.width = '0%'
    }, 350)
  }, 200)
}

// Inline script in the HTML body handles the pct counter for initial hard load.
// Only kick off the top bar here.
startAppProgress(false)

let vueApp = null;
const pinia = createPinia()

// Pinia calls `registerPiniaDevtools` → `setupDevtoolsPlugin` on every
// `app.use(pinia)`. With Turbo Drive a new Vue app is created on every
// navigation, so this fires repeatedly. The Vue DevTools browser-extension
// hook becomes stale after the first app unmounts, causing an uncaught
// "Cannot read properties of undefined (reading 'app')" promise rejection.
// Fix: allow devtools to register on the first install only. On subsequent
// installs, temporarily hide the global devtools hook so Pinia's
// `setupDevtoolsPlugin` call is a no-op, then restore it immediately.
const _piniaInstall = pinia.install.bind(pinia)
let _piniaDevtoolsDone = false
pinia.install = function (app) {
  if (_piniaDevtoolsDone) {
    // Vue DevTools may define __VUE_DEVTOOLS_GLOBAL_HOOK__ as a getter-only
    // property, making direct assignment throw. Use defineProperty to
    // temporarily shadow it; fall back gracefully if the descriptor is
    // non-configurable (e.g. certain browser extensions or locked environments).
    const savedHook = window.__VUE_DEVTOOLS_GLOBAL_HOOK__
    let suppressed = false

    try {
      Object.defineProperty(window, '__VUE_DEVTOOLS_GLOBAL_HOOK__', {
        configurable: true,
        get: () => undefined,
      })
      suppressed = true
    } catch (e) {
      console.warn('[pinia-devtools] Could not suppress __VUE_DEVTOOLS_GLOBAL_HOOK__ — proceeding without suppression. Devtools may emit a stale-app warning.', e)
    }

    try {
      _piniaInstall(app)
    } catch (e) {
      console.error('[pinia-devtools] Pinia install failed.', e)
    }

    if (suppressed) {
      try {
        Object.defineProperty(window, '__VUE_DEVTOOLS_GLOBAL_HOOK__', {
          configurable: true,
          get: () => savedHook,
        })
      } catch (e) {
        console.warn('[pinia-devtools] Could not restore __VUE_DEVTOOLS_GLOBAL_HOOK__.', e)
      }
    }
  } else {
    _piniaDevtoolsDone = true
    _piniaInstall(app)
  }
}

document.addEventListener("turbo:before-render", function () {
  if (vueApp !== null) {
    vueApp.unmount();
    vueApp = null;
  }
});

// After every Turbo page swap: re-show the overlay and restart the progress bar.
document.addEventListener("turbo:render", function () {
  const loader = document.getElementById('app-loader')
  if (loader) loader.classList.remove('app-loader-out')
  startAppProgress()
});

// Apply Highcharts theme globally — runs once, affects all chart() / mapChart() calls.
if (typeof Highcharts !== 'undefined') Highcharts.setOptions(HIGHCHARTS_THEME)

document.addEventListener("turbo:load", function () {
      const element = document.querySelector(point)
      if (element !== null && vueApp === null && element.innerHTML.trim() !== '') {
        const app = Vue.createApp({
          data() {
            return {count: 1}
          },
        })

        // Declare Global Variables
        let globalVariablesDiv = document.getElementById("global-variables");
        let globalVariables = globalVariablesDiv.getAttribute("data");
        for (const [key, value] of Object.entries(JSON.parse(globalVariables))) {
          window[key] = value;
        }

        app.use(VCalendar, {})
        app.use(pinia)

        // Public pages (profile, event booking) use Turbo for navigation, not Vue Router.
        // Installing the router on these pages causes history.replaceState conflicts with
        // Turbo Drive, making the URL revert to the previous page after navigation.
        const isPublicPage = element.dataset.publicPage === 'true'

        if (!isPublicPage) {
          app.use(router)

          // Unlock AudioContext on every SPA navigation — router.beforeEach fires
          // synchronously inside the user's click, giving us the cleanest gesture window.
          router.beforeEach(() => { unlockAudio() })

          // Keep sidebar active-link state in sync on every SPA navigation
          router.afterEach((to) => {
            useGlobalStore().setCurrentPath(to.path)
          })
        }

        // Init global store with user + app data from Rails globals
        const globalStore = useGlobalStore()
        globalStore.init()

        // Open ActionCable connection for the whole session (not just /notifications)
        if (globalStore.user.id) {
          useNotificationsStore().subscribeToChannel()
          globalStore.subscribeToUserChannel()
        }
        app.component('onboarding', Onboarding)
        app.component('public-event', PublicEvent)
        app.component('public-event-legacy', PublicEventLegacy)
        app.component('home-navbar', Navbar)
        app.component('home-features', HomeFeatures)
        app.component('availability', Availability)
        app.component('app-shell', AppShell)
        app.component('flash-message', FlashMessage)
        app.component('connected-calendars', ConnectedCalendars)
        app.component('copy-text', CopyText)
        app.component('event', Event)
        app.component('event-table', EventsTable)
        app.component('holiday', Holiday)
        app.component('stats-card', StatsCard)
        app.component('bookings-list', BookingsList)
        app.component('cancel-booking', CancelBooking)
        app.component('vue-confetti', VueConfetti)
        app.component('cookies-modal', CookiesModal)
        app.component('attendees-list', AttendeesList)
        app.component('integrations-list', IntegrationsList)
        app.component('integrate-external-payments', ExternalPayments)
        app.component('user-webhooks', Webhooks)
        app.component('home-secondary', HomeSecondaryFeatures)
        app.component('admin-users-list', AdminUsersList)
        app.component('admin-bugs-list', AdminBugsList)
        app.component('admin-verifications-list', AdminVerificationsList)
        app.component('my-calendar-page', MyCalendarIndexPage)
        app.component('calendar-settings', CalendarSettings)
        app.component('setting-profile', SettingProfile)
        app.component('bottom-modal', TimelydoBottomModal)
        app.component('bottom-nav', BottomNav)
        app.component('org-index', OrgIndex)
        app.component('org-onboarding', OrganizationOnboarding)
        app.component('public-message-form', MessageForm)
        app.component('troubleshoot', Troubleshoot)
        app.component('pricing-index', PricingIndex)
        app.component('social-icons', SocialIcons)
        app.component('admin-user-invitations', UserInvitations)
        app.component('admin-emails', AdminEmails)
        app.component('quill-editor', QuillEditor)
        app.component('new-event', NewEvent)
        app.component('app-sidebar', AppSidebar)
        app.component('sidebar-link', SidebarLink)
        app.component('events-page', EventsPage)
        vueApp = app;
        app.mount(point)

        requestAnimationFrame(() => {
          const loader = document.getElementById('app-loader')
          if (loader) loader.classList.add('app-loader-out')
          finishAppProgress()
        })
      }
    }
)
